import { Fuel, LayoutDashboard, ReceiptText, ShieldCheck, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Logo } from '@/components/brand/logo';
import { APP_META } from '@/config/app';

import { EscenaEstacion } from './escena-estacion';

interface Feature {
  icon: LucideIcon;
  title: string;
  description: string;
}

const FEATURES: readonly Feature[] = [
  {
    icon: LayoutDashboard,
    title: 'Operación en tiempo real',
    description: 'Turnos, ventas y cuadres al instante.',
  },
  {
    icon: Fuel,
    title: 'Combustible e inventario',
    description: 'Sondas de tanque y precios al día.',
  },
  {
    icon: ReceiptText,
    title: 'Reportes y liquidaciones',
    description: 'Listos para contabilidad y SUNAT.',
  },
  {
    icon: Users,
    title: 'Equipo y permisos',
    description: 'Cada usuario ve solo lo que necesita.',
  },
] as const;

/**
 * Panel de marca (lado izquierdo del login en pantallas grandes).
 *
 * Es puramente informativo; en móvil se oculta y el logo se muestra en el header.
 * Desde la última revisión el bloque de cuatro tarjetas **estáticas** que había
 * debajo del titular se reemplazó por:
 *
 * - una lista de fichas compactas que entran **escalonadas** (una detrás de otra,
 *   como los datos del panel de referencia), y
 * - la escena animada de la estación (`EscenaEstacion`), anclada al pie del panel,
 *   con el techo, los surtidores, el letrero de precios y el corte subterráneo de
 *   los tanques con sus sondas de telemedición.
 *
 * El titular y las cuatro capacidades (con su descripción) ocupan la parte alta
 * y el centro; la escena animada queda **pegada al pie** del panel, que es donde
 * el dibujo se lee mejor. Las capacidades entran escalonadas, una detrás de otra,
 * como los datos del panel de referencia.
 */
export function BrandPanel() {
  return (
    <aside className="relative hidden h-dvh overflow-hidden bg-ink-950 lg:flex lg:flex-col">
      {/* Fondo: degradado turquesa + halos animados + rejilla sutil */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-brand-600 via-brand-800 to-ink-950"
      />
      <div
        aria-hidden="true"
        className="absolute -left-24 -top-24 size-[28rem] animate-aurora rounded-full bg-brand-400/35 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-32 -right-20 size-[26rem] animate-aurora rounded-full bg-accent-400/25 blur-3xl [animation-delay:-7s]"
      />
      <div aria-hidden="true" className="absolute inset-0 bg-grid opacity-40" />

      {/* Contenido: mensaje arriba y capacidades en el centro */}
      <div className="relative z-10 flex flex-col p-8 pb-2 xl:p-10 xl:pb-3">
        {/* La versión y el copyright van con el logo, para dejarle el pie entero a la escena */}
        <div className="flex items-start justify-between gap-4">
          <Logo tone="light" size="md" tagline="Estaciones de servicio" />
          <p className="shrink-0 pt-1 text-right text-[11px] leading-relaxed text-white/50">
            <span className="font-mono">v{APP_META.version}</span>
            <br />© {new Date().getFullYear()} {APP_META.vendor}
          </p>
        </div>

        <div className="mt-6 max-w-lg xl:mt-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white/90 backdrop-blur-sm">
            <ShieldCheck className="size-3.5" aria-hidden="true" />
            Plataforma unificada de gestión
          </span>

          <h2 className="mt-4 text-3xl leading-tight font-bold tracking-tight text-balance text-white 2xl:text-4xl">
            Toda tu estación de servicio, en un solo panel.
          </h2>
        </div>

        {/*
         * Capacidades del sistema: cuatro tarjetas compactas (icono, título y
         * una línea de detalle) que llenan el centro del panel. Antes eran fichas
         * de una sola línea al pie y quedaba un hueco muerto entre el titular y
         * la escena.
         */}
        <ul className="mt-6 grid max-w-xl gap-3 sm:grid-cols-2 xl:mt-7">
          {FEATURES.map((feature, indice) => {
            const Icon = feature.icon;

            return (
              <li
                key={feature.title}
                className="esc-dato flex items-center gap-2.5 rounded-2xl border border-white/10 bg-white/[0.07] p-3 backdrop-blur-sm"
                style={{ animationDelay: `${0.55 + indice * 0.15}s` }}
              >
                <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-white/10">
                  <Icon className="size-4 text-brand-200" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-white">
                    {feature.title}
                  </span>
                  <span className="block truncate text-xs text-white/65">{feature.description}</span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>

      {/*
       * Escena animada de la estación (SVG + keyframes de CSS), **pegada al pie**.
       *
       * `flex-1` la lleva hasta el fondo del panel (es el último bloque, ya no hay
       * pie debajo) y el `max-h` en `vw` es su altura ideal: el panel ocupa ~52 %
       * de la ventana y el dibujo es 1200×640, así que `27.5vw` reproduce esa
       * proporción (0.52 / 1.875) y **la escena entra completa, sin recorte
       * lateral**. El `min-h` evita que desaparezca en ventanas bajas; en ese caso
       * el encuadre recorta por arriba, donde sólo están los cerros y el letrero.
       */}
      <div className="relative z-0 mt-auto max-h-[27.5vw] min-h-[190px] w-full flex-1 overflow-hidden">
        <EscenaEstacion />
      </div>
    </aside>
  );
}
